import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AnalyticsService } from '@common/services';
import { CommerceService } from '@common/services/commerce.service';
import { NgbActiveModal, NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { PAYMENT_REJECTED_NOTICE } from '../../constants/credit-line.constants';
import { FINA_PAGO_MOVIL_PHONE, FINA_PAGO_MOVIL_PHONE_DIGITS, FINA_PAYMENT_DATA_PAGO_MOVIL, FINA_PAYMENT_DATA_TRANSFERENCIA, FinaPaymentRow } from '../../constants/fina-payment-info';
import { PAGO_MOVIL_PREFIXES, SUDEBAN_BANKS_WITH_CODE, SudebanBank } from '../../constants/sudeban-banks';
import { BanescoTestData, Credito } from '../../models';
import { CreditosService } from '../../services/creditos.service';
import { visibleRejection } from '../../utils/abono-plan.util';
import { buildCreditErrorProps, buildPaymentProps, buildPaymentResultProps, trackCreditEvent } from '../../utils/credit-analytics.util';
import { caracasDayIso, DdMmYyyyParserFormatter, formatDdMmYyyy, isIsoDate, IsoStringDateAdapter, isoToDateStruct, localTodayIso } from '../../utils/creditos-datepicker';
import { FULL_REFERENCE_PATTERN, referenceDigitsMessage } from '../../utils/fina-payment-rows.util';
import { formatVesFromDigits, formatVesNumber, parseVesDisplay } from '../../utils/ves-amount-mask';

/**
 * Modal global de "acceso bloqueado por mora" — disparado por el interceptor cuando el
 * backend responde 423 Locked en cualquier endpoint de la app.
 *
 * Política v2: el cliente registra el pago directamente desde acá (input de referencia
 * + botón "Notificar pago"). Eso dispara el flujo normal de `payInstallment` (cuota
 * pasa a `verifying`); el bloqueo solo se levanta cuando admin verifica el pago.
 *
 * Bloquea navegación con backdrop estático mientras el crédito siga bloqueado.
 */
export interface CreditBlockedModalData {
    blocked: boolean;
    /** Abonos: la cuota vencida ya está cubierta y espera verificación de soporte (no hay que pagar más). */
    pendingVerification?: boolean;
    creditId?: string;
    activationFee: number;
    dueAmount: number;
    totalToUnblock: number;
    firstOverdueInstallmentNumber: number | null;
    dueDate: string | null;
    paymentDetails: any;
    gracePeriodDays?: number;
    /** Desembolso del crédito: un pago con fecha anterior no puede pagarlo (el API lo rechaza). */
    depositedAt?: string | null;
}

@Component({
    selector: 'fina-credit-blocked-modal',
    templateUrl: './credit-blocked-modal.component.html',
    styleUrls: ['./credit-blocked-modal.component.scss'],
    /**
     * ADM-678 (7ª iteración): mismo selector de fecha que `pay-modal` — el modelo
     * sigue siendo el string 'YYYY-MM-DD' del API; el cliente ve dd/mm/aaaa.
     */
    providers: [
        { provide: NgbDateAdapter, useClass: IsoStringDateAdapter },
        { provide: NgbDateParserFormatter, useClass: DdMmYyyyParserFormatter },
    ],
})
export class CreditBlockedModalComponent implements OnInit, OnDestroy {
    @Input() data!: CreditBlockedModalData;
    /**
     * FINA-2062 — modo viewer: si `true`, deshabilita el flujo de "Notificar pago" y muestra
     * leyenda "Solo el dueño de la cuenta puede pagar". El interceptor 423 (`credit-blocked.interceptor`)
     * lo setea según `userService.user$.value.isOwner` antes de abrir el modal. Backend también
     * rechaza con 403 si non-owner intenta `PUT /api/creditos/:id/pay`.
     */
    @Input() viewerMode: boolean = false;

    /** Datos bancarios de Fina para la transferencia — fuente única compartida con `pay-modal`. */
    /**
     * Card según el método: pago móvil se envía a un teléfono, transferencia a un
     * número de cuenta. Mismo criterio que en `pay-modal`.
     */
    get finaData(): ReadonlyArray<FinaPaymentRow> {
        return this.isPagoMovil ? FINA_PAYMENT_DATA_PAGO_MOVIL : FINA_PAYMENT_DATA_TRANSFERENCIA;
    }
    copiedKey = '';

    /** Referencia del pago — vincula al input. El botón se habilita cuando hay valor no vacío. */
    referenceNumber = '';
    submitting = false;
    /** Mensaje de éxito tras notificar el pago (cuota → verifying). */
    notifiedOk = false;
    /** Resultado de la verificación con el banco (título y mensaje del API) para la pantalla final. */
    resultTitle: string | null = null;
    resultMessage: string | null = null;
    /** Error textual si el POST falla, para mostrarlo dentro del modal sin cerrarlo. */
    errorMsg: string | null = null;
    /**
     * ADM-799: «Pago rechazado: <motivo>» si soporte rechazó el último pago o abono de la cuota vencida. El 423
     * no trae el historial: sale de `GET /creditos/current`, que api deja leer con el crédito bloqueado.
     * Misma regla y mismo texto que la tarjeta de la cuota (`visibleRejection`).
     */
    rejectionNotice: string | null = null;

    /** Comprobante opcional — sigue el mismo patrón del `pay-modal`. */
    comprobanteFile: File | null = null;
    /** FINA-3378: el comprobante es opcional; si falla la subida se notifica sin él. */
    comprobanteError = '';
    /**
     * Preview del comprobante. Es un `SafeUrl` (no string) porque generamos un blob: URL
     * con `URL.createObjectURL`, y el `SAFE_URL_PATTERN` de Angular 13 no incluye `blob:` —
     * sin bypass el `[src]` queda como `unsafe:blob:...` y rompe la imagen. Tracking del
     * blob crudo en `comprobantePreviewBlob` para revocarlo correctamente.
     */
    comprobantePreview: SafeUrl | null = null;
    private comprobantePreviewBlob: string | null = null;
    uploadingComprobante = false;

    constructor(
        public activeModal: NgbActiveModal,
        private creditosService: CreditosService,
        private sanitizer: DomSanitizer,
        private analyticsService: AnalyticsService,
        private commerceService: CommerceService,
    ) {}

    ngOnInit(): void {
        // ADM-678: bancos y data de prueba para el formulario de notificación.
        // Este modal lo abre el interceptor del 423, así que se carga acá y no en un
        // `open()` como el pay-modal.
        if (!this.viewerMode) this.loadPaymentFormData();
        // Monto de prueba (8ª iteración): precarga total × tasa en cuanto haya tasa.
        this.rateSub = this.vesRate$.subscribe(rate => this.applyAutoTestAmount(rate));
        // FINA-3412: el interceptor fija `viewerMode` antes del primer ciclo (lo mismo que usa la carga de arriba).
        trackCreditEvent(this.analyticsService, 'blockedModalViewed', { viewer_mode: this.viewerMode, pending_verification: this.pendingVerification });
    }

    ngOnDestroy(): void {
        this.rateSub?.unsubscribe();
    }

    /**
     * Tasa BCV USD del día (hidratada por auth al login/focus) — para mostrar el
     * equivalente en Bs. bajo cada monto. Si aún no cargó (null), la línea se oculta.
     */
    get vesRate$() {
        return this.commerceService.exchangeRateBCV$;
    }

    // ── ADM-678 (8ª iteración): monto de prueba en Bs., solo local/staging ───
    /**
     * Texto del equivalente en Bs. de la fila "Total" cuando es editable
     * (`testData.enabled`). Precargado con `total × tasa del día`; solo si el
     * tester lo edita (`testAmountDirty`) viaja como `testAmountVes`. Mismo trío
     * que en `pay-modal`.
     */
    testAmountVes = '';
    testAmountDirty = false;
    private rateSub: Subscription | null = null;

    /** Monto como número (el texto va formateado `1.234,56`), o `null` si no es > 0. */
    get testAmountVesNumber(): number | null {
        return parseVesDisplay(this.testAmountVes);
    }

    get testAmountVesInvalid(): boolean {
        return this.testAmountDirty && this.testAmountVesNumber === null;
    }

    /** Máscara de moneda: los dos últimos dígitos son decimales (`200000` → `2.000,00`). */
    onTestAmountInput(ev: Event): void {
        const input = ev.target as HTMLInputElement;
        const v = formatVesFromDigits(input.value);
        this.testAmountDirty = true;
        this.testAmountVes = v;
        if (input.value !== v) input.value = v;
    }

    resetTestAmount(): void {
        this.testAmountDirty = false;
        this.applyAutoTestAmount(this.commerceService.exchangeRateBCV$.value);
    }

    private applyAutoTestAmount(rate: number | null): void {
        if (this.testAmountDirty) return;
        const total = Number(this.data?.totalToUnblock || 0);
        this.testAmountVes = rate && rate > 0 && total > 0 ? formatVesNumber(total * rate) : '';
    }

    money(n: number): string {
        return `$${Number(n || 0).toFixed(2)}`;
    }

    /** La cuota vencida ya fue cubierta (pago o abonos): solo falta que soporte la verifique. */
    get pendingVerification(): boolean {
        return !!this.data?.pendingVerification;
    }

    get isPagoMovil(): boolean {
        return this.paymentMethod === 'pago_movil';
    }

    /**
     * ADM-678 (ciclo 2 QA): el formato de la referencia depende del método Y del
     * banco de origen, porque cada combinación va a una modalidad distinta de
     * Banesco (espejo de `validateLocally` en el API):
     *  - Pago móvil → modalidad 3: últimos 6 dígitos.
     *  - Transferencia Banesco→Banesco (0134) → modalidad 2: la referencia
     *    completa, 10 a 12 dígitos (ADM-810), porque el banco busca por la referencia entera.
     *  - Transferencia desde otro banco → modalidad 4: últimos 6 dígitos, junto
     *    con banco, fecha y cuenta.
     */
    get requiresFullReference(): boolean {
        return !this.isPagoMovil && this.bankCode === '0134';
    }
    get referencePattern(): string {
        return this.requiresFullReference ? FULL_REFERENCE_PATTERN : '\\d{6}';
    }
    get referencePlaceholder(): string {
        return this.requiresFullReference ? 'Ej. 00000151433' : 'Ej. 100129';
    }
    /** Cuántos dígitos escribir: siempre visible bajo el campo; en rojo si ya se tocó y no cumple. */
    get referenceError(): string {
        return referenceDigitsMessage(this.requiresFullReference);
    }
    /**
     * Solo dígitos; la referencia completa sin recortar (una de más de 12 marca el error en vez de
     * enviarse cortada, ADM-810) o los últimos 6, según el caso.
     */
    private sanitizeReference(raw: string): string {
        const digits = (raw || '').replace(/\D/g, '');
        return this.requiresFullReference ? digits : digits.slice(-6);
    }
    /**
     * Cambiar el banco puede cambiar el formato exigido (0134 ↔ otro): se
     * re-sanitiza lo escrito para que el campo no quede inválido en silencio.
     */
    onBankChanged(): void {
        if (this.referenceNumber) this.referenceNumber = this.sanitizeReference(this.referenceNumber);
    }

    selectMethod(method: 'pago_movil' | 'transferencia'): void {
        this.paymentMethod = method;
        // No arrastrar datos del método anterior: el teléfono que el otro no usa ni
        // la referencia, que cambia de formato entre métodos.
        this.phoneNumber = '';
        this.referenceNumber = '';
        this.resetTestAmount();
    }

    get canSubmit(): boolean {
        // viewerMode (non-owner): nunca puede enviar pago — el botón queda permanentemente deshabilitado.
        if (this.viewerMode) return false;
        if (this.pendingVerification) return false;
        // ADM-678: mismo formato que pay-modal — últimos 6 dígitos en pago móvil, la
        // referencia completa (10-12) en transferencia Banesco→Banesco.
        const ref = (this.referenceNumber || '').trim();
        if (!new RegExp('^' + this.referencePattern + '$').test(ref)) return false;
        // ADM-678: el API exige banco, teléfono y fecha para verificar con Banesco.
        // Sin estos tres, `payInstallment` responde 400 y el cliente bloqueado no
        // podría desbloquearse nunca.
        if (!this.bankCode) return false;
        // El teléfono solo existe en pago móvil: una transferencia no lo pide ni lo
        // envía (ver `doNotify`). Exigirlo siempre dejaba el botón bloqueado para
        // un cliente en mora que pagó por transferencia.
        if (this.isPagoMovil && !/^\d{7}$/.test(this.phoneNumber)) return false;
        // ADM-678 (7ª iteración): el `ngbDatepicker` escribe en el modelo el texto
        // crudo cuando no puede parsearlo; solo una fecha ISO real habilita el envío.
        if (!isIsoDate(this.paymentDate)) return false;
        // Un movimiento anterior al desembolso no puede pagar el crédito (el API responde 400).
        if (this.isDateBeforeDeposit) return false;
        // Monto de prueba editado pero inválido (solo local/staging).
        if (this.testAmountVesInvalid) return false;
        return !this.submitting && !this.uploadingComprobante && !this.notifiedOk;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // ADM-678 — datos que exige la verificación automática con el banco.
    //
    // Este modal notifica pagos igual que `pay-modal`, así que necesita los mismos
    // tres campos. Sin ellos el endpoint rechaza la notificación con 400, y este es
    // justamente el modal por el que un cliente bloqueado por mora se desbloquea.
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * ADM-678 (4ª iteración): método de pago. Acá no van las cards grandes del
     * `pay-modal` —este modal es más chico y ya está apretado— sino un toggle
     * compacto. El motivo de fondo es el mismo: lo que Fina publica son datos de
     * transferencia, y exigir un teléfono de pago móvil dejaba a un cliente
     * bloqueado sin forma de reportar su pago.
     */
    paymentMethod: 'pago_movil' | 'transferencia' = 'pago_movil';

    bankCode = '';
    phonePrefix = PAGO_MOVIL_PREFIXES[1];
    phoneNumber = '';
    /** 'YYYY-MM-DD' en hora local (antes era UTC: "mañana" después de las 20:00 en VE). */
    paymentDate = localTodayIso();
    banks: ReadonlyArray<SudebanBank> = SUDEBAN_BANKS_WITH_CODE;
    readonly phonePrefixes = PAGO_MOVIL_PREFIXES;

    /** Cuenta receptora de QA publicada por el API. `enabled: false` en producción. */
    testData: BanescoTestData = { enabled: false };

    /** Hoy en hora local, 'YYYY-MM-DD'. */
    get today(): string {
        return localTodayIso();
    }

    /** Máximo del `ngbDatepicker`: el banco no puede tener un movimiento futuro. */
    get todayStruct(): NgbDateStruct | null {
        return isoToDateStruct(this.today);
    }

    /** Día del desembolso (Caracas), mínimo de la fecha del pago. */
    get depositDay(): string | null {
        return caracasDayIso(this.data?.depositedAt);
    }

    get depositDayStruct(): NgbDateStruct | null {
        return isoToDateStruct(this.depositDay);
    }

    get depositDayLabel(): string {
        return formatDdMmYyyy(this.depositDay);
    }

    /** La fecha elegida es anterior al desembolso del crédito. */
    get isDateBeforeDeposit(): boolean {
        const min = this.depositDay;
        return !!min && isIsoDate(this.paymentDate) && this.paymentDate < min;
    }

    get fullPhone(): string {
        return `${this.phonePrefix}${this.phoneNumber}`;
    }

    /**
     * Datos de pago a mostrar. En staging son los de QA, que son los que el API
     * consulta — ver el mismo getter en `pay-modal`.
     */
    get displayedPaymentData(): ReadonlyArray<FinaPaymentRow> {
        const acc = this.testData.enabled ? this.testData.account : null;
        if (!acc) return this.finaData;

        const banco: FinaPaymentRow = { key: 'banco', label: 'BANCO', value: `${acc.bankName} (${acc.bankCode})` };
        const rif: FinaPaymentRow = { key: 'rif', label: 'RIF', value: acc.rif };

        // El teléfono destino no tiene equivalente de QA: los movimientos de prueba
        // entran a la cuenta y ese dato no participa de la verificación.
        if (this.isPagoMovil) {
            return [banco, { key: 'telefono', label: 'TELÉFONO', value: FINA_PAGO_MOVIL_PHONE, copyValue: FINA_PAGO_MOVIL_PHONE_DIGITS }, rif];
        }

        return [banco, { key: 'cuenta', label: 'NÚMERO DE CUENTA', value: acc.accountNumber }, rif];
    }

    get stagingWarningDetail(): string {
        const acc = this.testData.account;
        if (!acc) return '';
        return `Cuenta de calidad: ${acc.accountNumber} (${acc.bankName})`;
    }

    onPhoneInput(ev: Event): void {
        const input = ev.target as HTMLInputElement;
        const v = (input.value || '').replace(/\D/g, '').slice(0, 7);
        this.phoneNumber = v;
        if (input.value !== v) input.value = v;
    }

    private loadPaymentFormData(): void {
        this.creditosService.banks$().subscribe(banks => {
            this.banks = banks;
        });
        this.creditosService.banescoTestData$().subscribe(data => {
            this.testData = data;
        });
        this.loadRejectionNotice();
    }

    /** Sin cuota vencida o con el pago ya en verificación no hay nada que avisar. Si la lectura falla, el modal sigue sin el aviso. */
    private loadRejectionNotice(): void {
        const n = this.data?.firstOverdueInstallmentNumber;
        if (n == null || this.pendingVerification) return;
        this.creditosService.getCurrent$().subscribe({
            next: (res: { data?: Credito | null } | null) => {
                const credito = res?.data;
                if (!credito || (this.data.creditId && String(credito._id) !== String(this.data.creditId))) return;
                const rejection = visibleRejection((credito.installments ?? []).find(i => i?.number === n));
                this.rejectionNotice = rejection ? PAYMENT_REJECTED_NOTICE.text(rejection.note) : null;
            },
            error: () => {},
        });
    }

    /**
     * Sanitiza el input de número de referencia: solo dígitos, los últimos 6 (ADM-678).
     * Mismo handler que `pay-modal.onReferenceInput` — formato consistente entre flujos.
     * Si pegan la referencia completa se conserva el final, que es lo que el banco usa.
     */
    onReferenceInput(ev: Event): void {
        const input = ev.target as HTMLInputElement;
        const v = this.sanitizeReference(input.value);
        this.referenceNumber = v;
        if (input.value !== v) input.value = v;
    }

    onFileSelected(event: Event): void {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;
        this.comprobanteFile = file;
        this.comprobanteError = '';
        // Usamos `URL.createObjectURL` (blob:) en vez de `FileReader.readAsDataURL` (data:)
        // porque `data:image/avif;base64,...` lo rechaza el sanitizer (regex permite solo
        // bmp|gif|jpeg|jpg|png|tiff|webp). Pero `blob:` TAMBIÉN lo rechaza Angular 13 — el
        // `SAFE_URL_PATTERN` no incluye `blob:`. Por eso lo marcamos como confiable con
        // `bypassSecurityTrustUrl`: el blob lo creó el navegador a partir de un `File`
        // local del usuario, no hay vector XSS.
        this.revokePreview();
        this.comprobantePreviewBlob = URL.createObjectURL(file);
        this.comprobantePreview = this.sanitizer.bypassSecurityTrustUrl(this.comprobantePreviewBlob);
    }

    removeComprobante(): void {
        this.revokePreview();
        this.comprobanteFile = null;
        this.comprobanteError = '';
        this.comprobantePreview = null;
    }

    private revokePreview(): void {
        if (this.comprobantePreviewBlob) {
            URL.revokeObjectURL(this.comprobantePreviewBlob);
            this.comprobantePreviewBlob = null;
        }
    }

    /** Copia el valor al portapapeles + feedback visual de 1.5s. */
    copy(key: string, value: string): void {
        try {
            navigator.clipboard?.writeText(value);
            this.copiedKey = key;
            setTimeout(() => {
                if (this.copiedKey === key) this.copiedKey = '';
            }, 1500);
        } catch {
            // navegadores muy viejos: silencioso
        }
    }

    /**
     * Notifica el pago: dispara el flujo normal de `payInstallment` con la referencia
     * que ingresó el usuario (y opcionalmente el comprobante). La cuota pasa a `verifying`
     * y el equipo de gestión la verificará igual que cualquier otro pago. El bloqueo SE
     * MANTIENE hasta que el pago sea verificado (ver middleware `creditAccess` en api).
     */
    notifyPayment(): void {
        // Telemetría — capturamos al inicio para medir intención de notificar pago en el
        // caso de bloqueo (modal disparado por interceptor 423). El evento NO se dispara para
        // viewerMode (staff non-owner) porque `canSubmit` es false y el botón está deshabilitado.
        trackCreditEvent(this.analyticsService, 'pagoBloqueoNotificarClicked');
        if (!this.canSubmit || !this.data?.creditId || this.data.firstOverdueInstallmentNumber == null) return;
        this.errorMsg = null;

        // El comprobante se sube primero a S3 (bucket privado fina-loans) y se persiste el
        // `key` (no la URL — las URLs son presigned y expiran). Mismo patrón que `pay-modal`.
        // FINA-3378: es opcional; sin archivo, o si la subida falla, se notifica sin él.
        if (!this.comprobanteFile) {
            this.doNotify(undefined);
            return;
        }
        this.comprobanteError = '';
        this.uploadingComprobante = true;
        this.creditosService.uploadComprobante$(this.comprobanteFile, this.data.creditId).subscribe({
            next: res => {
                this.uploadingComprobante = false;
                this.doNotify(res.key);
            },
            error: () => {
                this.uploadingComprobante = false;
                this.doNotify(undefined);
            },
        });
    }

    private doNotify(comprobanteKey: string | undefined): void {
        this.submitting = true;
        // Lo que se envía (FINA-3412): nunca referencia, teléfono ni el comprobante, solo si hay.
        const sent = buildPaymentProps({
            mode: 'one',
            installmentsCount: 1,
            amountUSD: this.data?.totalToUnblock,
            paymentMethod: this.paymentMethod,
            bankCode: this.bankCode,
            hasComprobante: !!comprobanteKey,
        });
        this.creditosService
            .payInstallment$(this.data.creditId!, {
                installmentNumber: this.data.firstOverdueInstallmentNumber!,
                referenceNumber: this.referenceNumber.trim(),
                comprobanteKey,
                // ADM-678: obligatorios para la verificación con el banco. Sin ellos
                // el endpoint responde 400 y el cliente bloqueado nunca se desbloquea.
                bankCode: this.bankCode,
                paymentMethod: this.paymentMethod,
                ...(this.isPagoMovil ? { phone: this.fullPhone } : {}),
                paymentDate: this.paymentDate,
                // Solo pruebas (8ª iteración): el monto editado en "Total" viaja
                // únicamente en ambiente de pruebas Y si el tester lo tocó.
                ...(this.testData.enabled && this.testAmountDirty && this.testAmountVesNumber ? { testAmountVes: this.testAmountVesNumber } : {}),
            })
            .subscribe({
                next: res => {
                    this.submitting = false;
                    const v = res?.verification;
                    trackCreditEvent(this.analyticsService, 'paymentResult', { flow: 'unblock', ...sent, ...buildPaymentResultProps(v) });
                    // Refresca el estado en el servicio: la cuota quedó pagada, en verificación o en soporte.
                    this.creditosService.refreshAccessStatus$().subscribe({ error: () => {} });
                    // ADM-799: todo 2xx ya registró el reporte nuevo (aunque el banco no lo encuentre), así que el
                    // rechazo anterior deja de aplicar, igual que en la tarjeta de la cuota.
                    this.rejectionNotice = null;
                    if (v?.outcome === 'failed') {
                        // El banco no encontró el pago: el formulario sigue abierto para corregir los datos.
                        this.errorMsg = v.message + (v.caseId ? ` (caso ${v.caseId})` : '');
                        return;
                    }
                    this.notifiedOk = true;
                    this.resultTitle = v?.title || null;
                    this.resultMessage = v?.message || null;
                },
                error: (err: any) => {
                    this.submitting = false;
                    trackCreditEvent(this.analyticsService, 'paymentFailed', { flow: 'unblock', ...sent, ...buildCreditErrorProps(err), rate_changed: false });
                    this.errorMsg = err?.error?.message || 'No se pudo registrar la referencia. Inténtalo de nuevo.';
                },
            });
    }

    /** Cierra el modal — solo permitido tras notificar el pago (mientras espera verificación). */
    close(): void {
        this.activeModal.close(this.notifiedOk ? 'notified' : 'cancelled');
    }
}
